import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useQueryClient } from 'react-query';

import { Box, Button, Divider, IconButton, InputAdornment, MenuItem, useTheme } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

import { yupResolver } from '@hookform/resolvers/yup';
import { ControlledSelect } from '@frontend/features/common/components/ControlledSelect';
import { ControlledCheckbox } from '@frontend/features/common/components/ControlledCheckbox';
import { ControlledTextField } from '@frontend/features/common/components/ControlledTextField';
import { client } from '@frontend/features/common/services';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { QueryKeys } from '@frontend/features/common/types';
import { HotelIntegrationDto, ICreateHotelIntegration, IUpdateHotelIntegration } from '@bandido/shared/dtos';
import { HotelIntegrationProviderEnum } from '@bandido/shared/constants';
import {useEffect, useState} from 'react';

interface FormProps {
  onClose: () => void;
  integrationToEdit?: HotelIntegrationDto;
}

interface CreateHotelIntegrationFormInput {
  provider: HotelIntegrationProviderEnum;
  allowSeeByOthers: boolean;
  apiKey?: string;
  clientCode?: string;
  clientName?: string;
}

export const FormCreateOrEditHotelIntegration = ({ onClose, integrationToEdit }: FormProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const queryClient = useQueryClient();

  const [showApiKey, setShowApiKey] = useState(false);

  const schema = yup.object().shape({
    provider: yup
      .mixed<HotelIntegrationProviderEnum>()
      .oneOf(Object.values(HotelIntegrationProviderEnum))
      .required(t('common.form.required'))
      .default(undefined),
    allowSeeByOthers: yup.boolean().required(),
    apiKey: yup.string().when('provider', {
      is: (val: HotelIntegrationProviderEnum) => val === HotelIntegrationProviderEnum.TRAVELGATE,
      then: (schema) => schema.required(t('common.form.required')),
      otherwise: (schema) => schema.notRequired(),
    }),
    clientCode: yup.string().when('provider', {
      is: (val: HotelIntegrationProviderEnum) => val === HotelIntegrationProviderEnum.TRAVELGATE,
      then: (schema) => schema.required(t('common.form.required')),
      otherwise: (schema) => schema.notRequired(),
    }),
    clientName: yup.string().when('provider', {
      is: (val: HotelIntegrationProviderEnum) => val === HotelIntegrationProviderEnum.TRAVELGATE,
      then: (schema) => schema.required(t('common.form.required')),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const methods = useForm<CreateHotelIntegrationFormInput>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      provider: undefined,
      allowSeeByOthers: false,
      apiKey: '',
      clientCode: '',
      clientName: '',
    }
  });

  const {
    handleSubmit,
    control,
    watch,
    reset,
    formState: { isValid, isSubmitting, errors },
  } = methods;

  useEffect(() => {
    if (integrationToEdit) {
      reset({
        provider: integrationToEdit.provider,
        allowSeeByOthers: integrationToEdit.allowSeeByOthers,
        apiKey: integrationToEdit.rawCredentials?.apiKey as string ?? '',
        clientCode: integrationToEdit.rawCredentials?.clientCode as string ?? '',
        clientName: integrationToEdit.rawCredentials?.clientName as string ?? '',
      });
    }
  }, [integrationToEdit, reset]);

  const watchProvider = watch('provider');

  const onSubmit = async (data: CreateHotelIntegrationFormInput) => {
    const { apiKey, clientCode, clientName, provider, allowSeeByOthers } = data;

    const rawCredentials = provider === HotelIntegrationProviderEnum.TRAVELGATE ? { apiKey, clientCode, clientName } : {};

    try {
      if (integrationToEdit) {
        const payload: IUpdateHotelIntegration = {
          rawCredentials,
          allowSeeByOthers,
        };

        await client.put(`/hotel-integrations/${integrationToEdit.id}`, payload);
        addToast({
          message: t('hotelIntegrations.update.success'),
          color: 'success',
        });
      } else {
        const payload: ICreateHotelIntegration = {
          provider,
          rawCredentials,
          allowSeeByOthers,
        };

        await client.post('/hotel-integrations', payload);
        addToast({
          message: t('hotelIntegrations.create.success'),
          color: 'success',
        });
      }

      await queryClient.invalidateQueries([QueryKeys.getAllHotelIntegrations]);
      onClose();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          (integrationToEdit ? t('hotelIntegrations.edit.updatedError') : t('hotelIntegrations.create.error'));
      
      addToast({
        message: typeof errorMessage === 'string' ? errorMessage : t('common.error.generic'),
        color: 'error',
      });
    }
  };

  const handleToggleShowApiKey = () => {
    setShowApiKey((prev) => !prev);
  };

  return (
    <FormProvider {...methods}>
      <Box display="flex" flexDirection="column" gap={3} component="form" onSubmit={handleSubmit(onSubmit)}>
        <ControlledSelect
          control={control}
          name="provider"
          label={t('hotelIntegrations.form.provider.label')}
          labelId="provider-label"
          id="provider-select"
          variant="outlined"
          fullWidth
          error={!!errors.provider}
          helperText={errors.provider?.message}
        >
          {Object.values(HotelIntegrationProviderEnum).map((providerValue) => (
            <MenuItem key={providerValue} value={providerValue}>
              {providerValue}
            </MenuItem>
          ))}
        </ControlledSelect>

        {watchProvider === HotelIntegrationProviderEnum.TRAVELGATE && (
          <>
            <ControlledTextField
              control={control}
              name="apiKey"
              label={t('hotelIntegrations.form.apiKey.label')}
              variant="outlined"
              fullWidth
              type={showApiKey ? 'text' : 'password'}
              error={!!errors.apiKey}
              helperText={errors.apiKey?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle `${t('hotelIntegrations.form.apiKey.label')}`"
                      onClick={handleToggleShowApiKey}
                      edge="end"
                    >
                      {showApiKey ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <ControlledTextField
              control={control}
              name="clientCode"
              label={t('hotelIntegrations.form.clientCode.label')}
              variant="outlined"
              fullWidth
              error={!!errors.clientCode}
              helperText={errors.clientCode?.message}
            />
            <ControlledTextField
              control={control}
              name="clientName"
              label={t('hotelIntegrations.form.clientName.label')}
              variant="outlined"
              fullWidth
              error={!!errors.clientName}
              helperText={errors.clientName?.message}
            />
          </>
        )}

        <ControlledCheckbox
          control={control}
          name="allowSeeByOthers"
          label={t('hotelIntegrations.form.allowSeeByOthers.label')}
        />

        <Divider />
        <Box display="flex" justifyContent="flex-end" gap={theme.spacing(1)}>
          <Button variant="outlined" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isValid || isSubmitting}
          >
            {integrationToEdit ? t('common.actions.save') : t('common.actions.create')}
          </Button>
        </Box>
      </Box>
    </FormProvider>
  );
};
